import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ShippingPolicyPage() {
  return (
    <div data-testid="shipping-policy-page">
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-6">
              <Truck className="text-[#A67A5B]" size={20} />
              <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B]">Delivery Information</p>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-4 leading-tight">
              Shipping Policy
            </h1>
            <p className="font-sans text-sm text-[#7A736E] mb-12">Last updated: April 2026</p>

            <div className="space-y-10 font-sans text-[#2A2421]/80 leading-relaxed">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Order Processing</h2>
                <p className="text-base">
                  Orders placed through our Shopify store are typically processed within 2&ndash;5 business days. During high-demand periods or special promotions, processing may take slightly longer. You will receive a confirmation email once your order is placed.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Shipping Times</h2>
                <p className="text-base mb-3">Estimated shipping times after processing:</p>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#7A736E]">
                  <li><strong>Standard Shipping (US):</strong> 5&ndash;10 business days</li>
                  <li><strong>Expedited Shipping (US):</strong> 2&ndash;5 business days (where available)</li>
                  <li><strong>International Shipping:</strong> 10&ndash;20 business days (availability may vary)</li>
                </ul>
                <p className="text-sm text-[#7A736E] mt-3">
                  Please note that shipping times are estimates and may vary depending on your location and carrier availability.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Amazon Orders</h2>
                <p className="text-base">
                  Products purchased directly through Amazon are shipped and fulfilled by Amazon. Shipping times, tracking, and delivery are managed entirely by Amazon and are subject to their shipping policies and your selected shipping method.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Order Tracking</h2>
                <p className="text-base">
                  Once your order ships, you will receive a tracking number via email. You can use this number to track your package and view estimated delivery dates. If you do not receive tracking information within 7 business days of your order, please reach out to us.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Shipping Delays</h2>
                <p className="text-base">
                  Occasionally, shipping may be delayed due to carrier issues, weather, or high order volume. We appreciate your patience and will do our best to keep you informed. If your order is significantly delayed, please contact us and we will help resolve the situation.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Shipping Costs</h2>
                <p className="text-base">
                  Shipping costs are calculated at checkout based on your location and selected shipping method. We may offer free shipping promotions from time to time &mdash; follow us for the latest updates.
                </p>
              </div>

              <div className="pt-6 border-t border-[#E8E1D9]">
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Questions About Your Order?</h2>
                <p className="text-base mb-4">
                  If you have any questions about shipping or need help with an order, we are happy to assist.
                </p>
                <Link
                  to="/contact?inquiry=shipping_policy"
                  data-testid="shipping-contact-link"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#A67A5B] hover:text-[#8C654A] transition-colors"
                >
                  Contact us here &rarr;
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
